import {inject, Injectable} from "@angular/core";
import {ErrorRepo} from "../../repositories/api/error.repo";
import {LogLevel} from "../logger/log-level.enum";
import {HttpErrorResponse} from "@angular/common/http";
import {FrontendErrorDto} from "../../repositories/dto/frontend-error.dto";
import {environment} from "../../../environments/environment";
import {StateService} from "../state/state.service";
import {OrgDto} from "../../repositories/dto/org.dto";
import {AuthService} from "../auth/auth.service";

@Injectable({providedIn: 'root'})
export class ErrorService {

  private readonly stateService = inject(StateService);
  private readonly errorRepo = inject(ErrorRepo);
  private readonly authService = inject(AuthService);
  private readonly enabled: boolean = environment.api.logging.enabled;
  private readonly interval: number = environment.api.logging.interval;

  private errors: FrontendErrorDto[] = [];
  private working: boolean = false;
  private org: OrgDto | undefined;

  constructor() {
    this.stateService.getOrg().subscribe((o) => this.org = o);
    if (this.enabled) {
      setInterval(() => {
        if (!this.working && this.errors.length > 0) {
          this.submitErrors();
        }
      }, this.interval);
    }
  }

  public error(level: LogLevel, message: string, clazz: string, type?: string, code?: string | number): void {
    this.errors.push(new FrontendErrorDto({level, message, clazz, type, code, orgId: this.org?.id}));
    this.submitErrors();
  }

  private submitErrors(): void {
    // Without a session the endpoint answers 401 anyway - keep the errors queued and let the interval retry them once the user is authenticated.
    if (!this.working && this.errors.length > 0 && this.authService.isAuthenticated()) {
      this.working = true;
      // Take the batch out of the queue so errors raised while the request is in flight are not dropped on success.
      const batch: FrontendErrorDto[] = this.errors;
      this.errors = [];
      this.errorRepo.submitErrors(batch).subscribe({
        next: () => this.working = false,
        error: (err: HttpErrorResponse) => {
          this.errors = [...this.buildRetained(batch, err), ...this.errors];
          this.working = false;
        },
        complete: () => this.working = false
      });
    }
  }

  // Unreachable (status 0, also a request cut off by a page load): keep the batch for the next attempt, without an entry
  // of its own - one per failed attempt piled up while core was down. 401/403 means reporting is not authorised (expired
  // token, logout in progress): drop silently. Anything else: drop the batch and leave a single entry saying so.
  private buildRetained(batch: FrontendErrorDto[], err: HttpErrorResponse): FrontendErrorDto[] {
    let retained: FrontendErrorDto[] = [];
    if (err.status === 0) {
      retained = batch;
    } else if (err.status !== 401 && err.status !== 403) {
      retained = [new FrontendErrorDto({
        level: LogLevel.ERROR,
        message: "Error when transmitting errors to the backend: " + err.message,
        clazz: "ErrorService",
        type: err.name,
        code: err.status,
        orgId: this.org?.id
      })];
    }
    return retained;
  }
}
